import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
  alpha,
  Skeleton,
} from '@mui/material'
import { Building2, ExternalLink, Calendar, MapPin } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { backendClient } from '../../../api/backend'

// Color map for announcement types (same as AnnonceCard.js)
const TYPE_COLORS = {
  constitution: '#22c55e',
  dissolution: '#ef4444',
  modification_du_capital: '#f59e0b',
  cession_de_parts: '#8b5cf6',
  changement_de_gerant: '#3b82f6',
  transfert_de_siege: '#06b6d4',
  fusion: '#ec4899',
  transformation: '#14b8a6',
  prorogation: '#64748b',
  dissolution_anticipee: '#dc2626',
  modification_statuts: '#f97316',
}

function AnnonceRow({ annonce, onClick }) {
  const theme = useTheme()
  const { t } = useTranslation()
  const goldColor = theme.palette.primary.main
  const typeColor = TYPE_COLORS[annonce.type_annonce] || goldColor

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
    } catch {
      return ''
    }
  }

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        gap: 1.5,
        py: 1.25,
        px: 1.5,
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background 0.15s ease',
        '&:hover': {
          background: alpha(goldColor, 0.04),
        },
        '&:not(:last-child)': {
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '8px',
          background: alpha(typeColor, 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Building2 size={16} color={typeColor} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: theme.palette.text.primary,
            fontSize: '0.8rem',
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            mb: 0.25,
          }}
        >
          {annonce.denomination || t('unnamed_company')}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          {/* Type badge */}
          <Box
            sx={{
              px: 0.75,
              py: 0.1,
              borderRadius: '4px',
              background: alpha(typeColor, 0.1),
              border: `1px solid ${alpha(typeColor, 0.2)}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: typeColor,
                fontSize: '0.6rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {t(`annonce_type.${annonce.type_annonce}`, annonce.type_annonce?.replace(/_/g, ' '))}
            </Typography>
          </Box>

          {/* Date */}
          {annonce.date_publication && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Calendar size={10} color={theme.palette.text.disabled} />
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.disabled, fontSize: '0.65rem' }}
              >
                {formatDate(annonce.date_publication)}
              </Typography>
            </Box>
          )}

          {/* City */}
          {annonce.ville && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <MapPin size={10} color={theme.palette.text.disabled} />
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.disabled, fontSize: '0.65rem' }}
              >
                {annonce.ville}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

function ListSkeleton({ rows = 4 }) {
  return (
    <Box sx={{ px: 1.5, py: 1 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1.5, py: 1.25 }}>
          <Skeleton variant="rectangular" width={36} height={36} sx={{ borderRadius: '8px', flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="75%" height={14} />
            <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5 }}>
              <Skeleton variant="rectangular" width={60} height={16} sx={{ borderRadius: '4px' }} />
              <Skeleton variant="text" width={70} height={12} />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  )
}

const AnnoncesWidget = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const goldColor = theme.palette.primary.main

  const [annonces, setAnnonces] = useState([])
  const [totalAnnonces, setTotalAnnonces] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchLatest() {
      setLoading(true)
      try {
        const [response, stats] = await Promise.all([
          backendClient.searchLegalNotices({ page: 1, page_size: 8 }),
          backendClient.getLegalNoticesStats(),
        ])
        setAnnonces(response.items ?? [])
        setTotalAnnonces(stats.total_announcements ?? 0)
      } catch (err) {
        console.error('Error fetching dashboard annonces:', err)
        setAnnonces([])
      } finally {
        setLoading(false)
      }
    }
    fetchLatest()
  }, [])

  const cardSx = {
    elevation: 0,
    borderRadius: '12px',
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    boxShadow: theme.palette.mode === 'dark'
      ? '0 2px 8px rgba(0, 0, 0, 0.3)'
      : '0 2px 8px rgba(0, 0, 0, 0.04)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  }

  return (
    <Card elevation={0} sx={cardSx}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Building2 size={15} color={goldColor} />
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: '0.82rem', lineHeight: 1.2 }}
            >
              {t('annonces_legales') || 'Annonces L\u00E9gales'}
            </Typography>
            {!loading && totalAnnonces > 0 && (
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary, fontSize: '0.68rem' }}
              >
                {totalAnnonces.toLocaleString('fr-FR')} {t('total_announcements_label') || 'annonces'}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1 }} />
          <Box
            onClick={() => navigate('/legal-notice')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.4,
              cursor: 'pointer',
              color: goldColor,
              '&:hover': { opacity: 0.7 },
            }}
          >
            <ExternalLink size={11} />
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
              {t('view_all') || 'View all'}
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ maxHeight: 380, overflowY: 'auto', '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-track': { background: 'transparent' }, '&::-webkit-scrollbar-thumb': { background: alpha(theme.palette.primary.main, 0.2), borderRadius: '4px' } }}>
          {loading ? (
            <ListSkeleton rows={6} />
          ) : annonces.length > 0 ? (
            <Box sx={{ pb: 1 }}>
              {annonces.map((annonce) => (
                <AnnonceRow
                  key={annonce.annonce_id}
                  annonce={annonce}
                  onClick={() => navigate(`/legal-notice/annonce/${encodeURIComponent(annonce.annonce_id)}`)}
                />
              ))}
            </Box>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Building2 size={28} color={theme.palette.text.disabled} />
              <Typography
                variant="body2"
                sx={{ mt: 1, color: theme.palette.text.disabled, fontSize: '0.8rem' }}
              >
                {t('no_annonces_available') || 'No announcements available'}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

export default AnnoncesWidget
